<template>
  <div class="audience-hub-table">
    <v-container fluid>
      <v-row
        justify="start"
        dense
        no-gutters
      >
        <!-- <label style="padding: 10px 5px 0 0">Targets:</label> -->
        <v-col md="2">
          <v-select
            :items="targetFilterOptions"
            item-text="name"
            item-value="value"
            v-model="queryOption"
            label="Targets"
            dense
            outlined
            @input="onTargetFilterChange"
            hide-details="auto"
          ></v-select>
        </v-col>
        <v-col>
          <v-chip
            class="ma-1"
            color="teal"
            text-color="white"
            close
            @click:close="clearFilter(filter)"
            v-for="filter in appliedFilters"
            :key="filter.name"
          >
            {{ filter.name }} {{ filter.value }}
          </v-chip>
        </v-col>
      </v-row>
    </v-container>
    <ag-grid-vue
      :defaultColDef="defaultColDef"
      :frameworkComponents="frameworkComponents"
      :columnDefs="columnDefs"
      :getRowNodeId="getRowNodeId"
      :masterDetail="true"
      :detailCellRenderer="audienceDetailRenderer"
      :getRowHeight="detailRowHeight"
      :isExternalFilterPresent="isExternalFilterPresent"
      :doesExternalFilterPass="doesExternalFilterPass"
      :tooltipShowDelay="0"
      rowModelType="serverSide"
      :blockLoadDebounceMillis="500"
      :enableRangeSelection="true"
      :allowContextMenuWithControlKey="true"
      :getContextMenuItems="getContextMenuItems"
      overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Audiences Loading...</span>"
      overlayNoRowsTemplate="<div class='ag-overlay-loading-center' style='background-color: white;'><i class='far fa-frown'>0 Records Returned</i></div>"
      style="width: 100%; height: 100%"
      class="ag-theme-material"
      rowHeight="45"
      @gridReady="onGridReady"
      @filterChanged="onFilterChanged"
      @columnEverythingChanged="onColumnEverythingChanged"
    >
    </ag-grid-vue>
    <re-posting-modal />
    <delete-target-modal />
    <append-target-modal />
    <download-pid-list-modal title="Download PID Lists" />
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue'
  import 'ag-grid-enterprise'
  import TargetRenderer from '@/appComponents/targets/TargetRenderer'
  import TargetNameRenderer from '@/appComponents/targets/TargetNameRenderer'
  import DestinationsRenderer from '@/appComponents/targets/DestinationsRenderer'
  import AudienceDetailRenderer from '@/appComponents/targets/AudienceDetailRenderer'
  import RePostingModal from '@/appComponents/targets/components/modals/RePostingModal'
  import {
    dateFormatter,
    targetTypeToAbbrev,
    isTargetOwnedBy,
    isActivationBlocked,
    targetRouteHandler,
  } from '@/helpers'
  import SimpleButton from '@/components/common/simpleButton'
  import {
    ACTIVATION_STATUS,
    TARGET_TYPE,
    SOURCES_FILTER,
    DESTINATIONS_FILTER,
    actions,
    DESTINATIONS,
    LAS_TYPES,
    TARGET_ACTIONS,
    INTENTS,
  } from '@/appLogic/targets/Constants'
  import DeleteTargetModal from '@/appComponents/targets/components/DeleteTargetModal'
  import DownloadPidListModal from '@/appComponents/targets/components/DownloadPidListModal'
  import AppendTargetModal from '@/appComponents/targets/components/NPowerAppendTargetModal'
  import Tooltip from '@/components/common/tooltip'
  import {
    SET_UPLOAD_MODAL_OPTIONS,
    APPEND_PID,
    SET_STEPPER_DATA_SOURCE,
    SEND_NPOWER_DOWNSTREAM,
    SEND_COMSCORE_DOWNSTREAM,
  } from '@/appLogic/targets/createTarget/store/actions'
  import {
    ARCHIVE_TARGET,
    UNARCHIVE_TARGET,
    REFRESH_TABLE,
    SET_COLUMN_FILTERS,
  } from '@/views/audienceHub/store/actions'
  import { SET_DASHBOARD_FILTER, SET_DASHBOARD_TARGET_FILTER } from '@/store/modules/applicationResources/constants'
  import { SET_BACK_ROUTE_OVERRIDE } from '@/appLogic/route/store/actions'
  import {
    CLEAR_TARGET_DOWNLOAD,
    SHOW_DELETE_TARGET_MODAL,
    SET_CURRENT_TARGET,
    DOWNLOAD_LAS_TARGET,
    DOWNLOAD_TARGET_DEFINITION,
    SET_SHOW_APPEND_MODAL,
    SET_SHOW_PID_DOWNLOAD_MODAL,
    SHOW_REPOSSTING_MODAL,
  } from '@/appLogic/targets/currentTarget/store/actions'
  import { capitalize, startCase, cloneDeep } from 'lodash'
  import { fetchTargets } from '@/api'
  import { EventBus } from '@/api/eventBus'
  import SearchSetFilter from '@/appComponents/audienceHubDashboard/SearchSetFilter'
  import AddressableStatusIndicator from '@/appComponents/audienceHubDashboard/AddressableStatusIndicator'
  import Vue from 'vue'

  /**
   * FETCHING TARGETS
   *  - occurs on onGridReady() - see datasource
   *
   * FILTERS
   *  the filter is controled by the $router look for $router.push() methods for when the
   *  filter is being set
   *  filters -- this is the primaryu model of the current filter, it is a prop from the parent
   *      this comes from the router route.query
   *  watch on filters -- gets called when the filters change
   *  getFilterModelFromQuery() -- takes filters and creates the filter model
   *  this.gridApi.setFilterModel() -- set the grid filter call
   *  setInitialFilter() -- set the initial filter -- Deprecated by AM-3571
   *  onFilterChanged() when the filter changes on the Grid
   * */

  const DEFAULT_SEARCH_FIELDS = ['advertiserBrand', 'username', 'display_name', 'cae_id']

  const targetFilterOptions = [
    {
      name: 'My Targets',
      value: actions.MY_TARGETS,
    },
    {
      name: 'All Targets',
      value: actions.ALL_TARGETS,
    },
    {
      name: 'Archived Targets',
      value: actions.ARCHIVED_TARGETS,
    },
    {
      name: 'My Archived Targets',
      value: actions.MY_ARCHIVED_TARGETS,
    },
  ]

  const OPTIONS = {
    ACTIVATE: 'ACTIVATE',
    OPEN: 'OPEN',
    DUPLICATE: 'DUPLICATE',
    ARCHIVE: 'ARCHIVE',
    UNARCHIVE: 'UNARCHIVE',
    GRAPH: 'GRAPH',
    SETTINGS: 'SETTINGS',
    DELETE: 'DELETE',
    RE_POST: 'RE_FORECAST',
    DEFINITION: 'DEFINITION',
    PID_DOWNLOAD: 'PID_DOWNLOAD',
    APPEND: 'APPEND',
    UPDATE_LAS_MODAL: 'UPDATE_LAS_MODAL',
    UPDATE_CAMPAIGN_DATA_MODAL: 'UPDATE_CAMPAIGN_DATA_MODAL',
    SEND_DOWNSTREAM: 'SEND_DOWNSTREAM',
  }

  export default {
    components: {
      AgGridVue,
      TargetRenderer,
      TargetNameRenderer,
      DestinationsRenderer,
      SimpleButton,
      DeleteTargetModal,
      AppendTargetModal,
      DownloadPidListModal,
      RePostingModal,
      Tooltip,
      AddressableStatusIndicator,
    },
    props: {
      filters: null,
      searchFields: { type: Array, default: () => DEFAULT_SEARCH_FIELDS },
      routeParamsName: '',
      nielsenDatasources: {},
      C360Datasources: {},
    },
    data() {
      return {
        gridApi: null,
        columnDefs: null,
        targetFilterOptions,
        frameworkComponents: null,
        defaultColDef: {
          sortable: true,
          filter: true,
          filterParams: { newRowsAction: 'keep' },
          tooltipComponent: 'addressableStatusIndicator',
        },
        audienceDetailRenderer: null,
        detailRowHeight: null,
        popover: null,
        activeRowId: null,
        selectedTarget: null,
        targetTypeToAbbrev: targetTypeToAbbrev,
        defaultTargetFilterOption: {
          name: 'My Targets',
          value: actions.MY_TARGETS,
        },
        appliedFilters: [],
        forceUpdateFilterValues: false,
      }
    },
    beforeMount() {
      const columnDefs = [
        {
          headerName: 'Name',
          field: 'display_name',
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount: true,
            innerRendererFramework: 'TargetNameRenderer',
            suppressEnterExpand: true,
          },
          filter: 'agTextColumnFilter',
          flex: 5,
          cellClass: 'audience-hub-table__text-cell',
        },
        {
          headerName: 'Source',
          field: 'datasources',
          valueGetter: params => {
            if (!params.data || !params.data.datasources) return ''
            return params.data.datasources.sort((a, b) => (a > b ? 1 : -1))
          },
          cellRendererFramework: 'TargetRenderer',
          filter: 'searchSetFilter',
          filterParams: {
            filterType: 'set',
            values: Object.values(SOURCES_FILTER).sort((a, b) => (a.name > b.name ? 1 : -1)),
          },
          flex: 2.5,
          cellClass: 'audience-hub-table__text-cell',
          autoHeight: true,
        },
        {
          headerName: 'AM ID',
          field: 'cae_id',
          filter: 'agNumberColumnFilter',
          flex: 1.5,
          sort: 'desc',
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 76,
        },
        {
          headerName: 'Advertiser/Brand',
          field: 'advertiserBrand',
          valueGetter: params => ((params.data || {}).advertiser || {}).Name,
          filter: 'agTextColumnFilter',
          flex: 2.5,
          cellClass: 'audience-hub-table__text-cell',
        },
        {
          headerName: 'UE',
          field: 'audience_size',
          valueFormatter: params => this.formatSmallNumber(params.value),
          filter: 'agNumberColumnFilter',
          flex: 1,
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 76,
        },
        {
          headerName: 'DCR Partner',
          field: 'dcr_partner',
          valueGetter: params => {
            if (!params.data || !params.data.dcr_partner) return ''
            return params.data.dcr_partner.sort((a, b) => (a > b ? 1 : -1))
          },
          filter: 'agTextColumnFilter',
          flex: 2.5,
          cellClass: 'audience-hub-table__text-cell',
        },
        {
          headerName: 'Created',
          field: 'created',
          filterValueGetter: params => {
            const value = params.data[params.colDef.field]
            if (value) {
              let date = new Date(value)
              date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
              return date
            }

            return ''
          },
          valueFormatter: params => this.formatDate(params.value),
          filter: 'agDateColumnFilter',
          filterParams: { debounceMs: 300 },
          flex: 1.7,
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 89,
        },
        {
          headerName: 'Created By',
          colId: 'created_by',
          field: 'created_by',
          valueGetter: params => this.getNameFromUsername((params.data || {}).username),
          filter: 'agTextColumnFilter',
          flex: 3,
          cellClass: 'audience-hub-table__text-cell',
        },
        {
          headerName: 'Posted Through',
          field: 'last_posting',
          filterValueGetter: params => {
            const value = params.data[params.colDef.field]
            if (value) {
              let date = new Date(value)
              date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
              return date
            }
            return ''
          },
          valueFormatter: params => {
            let t = params.data
            let v = params.value ? this.formatDate(params.value) : ''
            if (!t) return v
            if (
              t.target_datasource === this.nielsenDatasources.COMSCORE ||
              t.target_datasource === this.nielsenDatasources.NIELSEN
            ) {
              if (t.activation_status === ACTIVATION_STATUS.ACTIVATED) {
                v = ''
              }
            }
            return v
          },
          filter: 'agDateColumnFilter',
          filterParams: { debounceMs: 300 },
          flex: 1.875,
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 130,
        },
        {
          headerName: 'Destination',
          field: 'destinations',
          valueGetter: params => {
            if (!params.data || !params.data.destinations) return ''
            return params.data.destinations.sort((a, b) => (a > b ? 1 : -1))
          },
          filter: 'searchSetFilter',
          filterParams: {
            filterType: 'set',
            values: Object.values(DESTINATIONS_FILTER).sort((a, b) => (a.name > b.name ? 1 : -1)),
          },
          cellRendererFramework: 'DestinationsRenderer',
          flex: 2.5,
          cellClass: 'audience-hub-table__text-cell',
          autoHeight: true,
        },
        {
          headerName: 'C360 ID',
          field: 'c360_id',
          filter: 'agNumberColumnFilter',
          flex: 1.25,
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 76,
        },
        {
          headerName: 'Status',
          field: 'activation_status',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: params => {
              const values = Object.values(ACTIVATION_STATUS).sort((a, b) => (a > b ? 1 : -1))
              params.success(values)
            },
          },
          flex: 1.5,
          cellClass: 'audience-hub-table__text-cell',
          minWidth: 127,
          tooltipField: 'activation_status',
        },
      ]

      this.audienceDetailRenderer = 'audienceDetailRenderer'
      this.detailRowHeight = params => {
        if (params.node && params.node.detail) {
          const data = params.node.data
          const hasDestination = data.destinations && data.destinations.length > 0
          const isAddressableAudience =
            hasDestination &&
            (data.destinations.includes(DESTINATIONS.DIRECTV) ||
              data.destinations.includes(DESTINATIONS.VIZIO) ||
              data.destinations.includes(DESTINATIONS.CANOE_LINEAR) ||
              data.destinations.includes(DESTINATIONS.CANOE_VOD))
          let height = 332

          switch (data.activation_status) {
            case ACTIVATION_STATUS.WORKING:
              if (isAddressableAudience) {
                height = 408
              } else if (hasDestination) {
                height = 380
              } else if (!hasDestination) {
                height = 296
              }
              break

            case ACTIVATION_STATUS.FAILED:
              if (data.am_data && data.am_data.flightStartDate && data.am_data.flightEndDate) {
                height = 408
              } else if (
                data.destinations.includes(DESTINATIONS.LAKE5) ||
                data.destinations.includes(DESTINATIONS.FORESITE)
              ) {
                height = 646
              } else {
                height = 400
              }
              break

            case ACTIVATION_STATUS.LIVE:
              height = 400
              if (data.destinations.includes(DESTINATIONS.LAKE5) && data.target !== TARGET_TYPE.VIDEOAMP) {
                height = 582
              } else if (data.destinations.includes(DESTINATIONS.FORESITE)) {
                height = 646
              }
              break

            case ACTIVATION_STATUS.ACTIVATED:
              if (
                (data.target === TARGET_TYPE.LAS && !data.audience_size) ||
                data.destinations.includes(DESTINATIONS.CANOE_LINEAR) ||
                data.destinations.includes(DESTINATIONS.CANOE_VOD)
              ) {
                height = 464
              } else {
                height = 400
              }
              break

            case ACTIVATION_STATUS.SUBMITTED:
              height = 400
              if (
                data.destinations.includes(DESTINATIONS.CANOE_LINEAR) ||
                data.destinations.includes(DESTINATIONS.CANOE_VOD)
              ) {
                height = 520
              }
              break

            default:
              break
          }

          return height
        }
      }

      this.columnDefs = columnDefs.map(column => {
        column.hide = false
        return column
      })
      this.frameworkComponents = {
        searchSetFilter: SearchSetFilter,
        addressableStatusIndicator: Vue.extend(AddressableStatusIndicator),
        audienceDetailRenderer: Vue.extend(AudienceDetailRenderer),
        // searchSetFilter: SearchSetFilter
      }
    },
    computed: {
      queryOption: {
        get() {
          let initialValue = actions.MY_TARGETS
          if (this.$route.params.name) {
            if (this.filters.archived && this.filters.archived.toString() === 'true') {
              initialValue = actions.MY_ARCHIVED_TARGETS
            } else {
              initialValue = actions.MY_TARGETS
            }
          } else if (this.filters && this.filters.archived && this.filters.archived.toString() === 'true') {
            initialValue = actions.ARCHIVED_TARGETS
          } else {
            initialValue = actions.ALL_TARGETS
          }

          return targetFilterOptions.find(option => {
            return option.value === initialValue
          })
        },
        set(newVal) {
          // no action
        },
      },
      hasPublishPermissions() {
        return this.$store.getters.hasPublishPermissions
      },
      hasCreateEditPermissions() {
        return this.$store.getters.hasCreateEditPermissions
      },
      hasArchivePermissions() {
        return this.$store.getters.hasArchivePermissions
      },
      // isResearchProfileUser () {
      //   const researchProfilePerms = [
      //     PERMISSIONS_OLD.AD_SALES_AMRLD_SYMPHONY_CAE,
      //     PERMISSIONS_OLD.RESEARCH_CREATE_EDIT
      //   ]
      //   return JSON.stringify(researchProfilePerms) === JSON.stringify(this.permissions)
      // },
      // isMarketingProfileUser () {
      //   const marketingProfilePerms = [
      //     PERMISSIONS_OLD.MARKETING_CREATE_EDIT,
      //     PERMISSIONS_OLD.MARKETING_MRI_SYMPHONY_PHOENIX
      //   ]
      //   return JSON.stringify(marketingProfilePerms) === JSON.stringify(this.permissions)
      // },
      refreshTable() {
        return this.$store.getters.refreshTable
      },
      username() {
        return this.$store.getters.username
      },
      permissions() {
        return this.$store.state.auth.permissions || []
      },
      hasViewPermissions() {
        return this.$store.getters.hasViewPermissions || false
      },
      // Deprecated by AM-3571
      // initialColumnsFromPermissions () {
      //   const columns = new Set()

      //   if (
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_MRI_SYMPHONY_CAE) ||
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_NCS_SYMPHONY_CAE) ||
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_AMRLD_SYMPHONY_CAE) ||
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_SYMPHONY_CAE)
      //   ) {
      //     columns.add('datasources')
      //     columns.add('target')
      //     columns.add('display_name')
      //     columns.add('advertiserBrand')
      //     columns.add('audience_size')
      //     columns.add('target_datasource')
      //     columns.add('viewing_stream')
      //     columns.add('cae_id')
      //     columns.add('created')
      //     columns.add('created_by')
      //     columns.add('flightstartdate')
      //     columns.add('flightenddate')
      //     columns.add('destinations')
      //     columns.add('last_posting')
      //     columns.add('activation_status')
      //   }

      //   if (
      //     this.permissions.includes(PERMISSIONS_OLD.MARKETING_MRI_SYMPHONY_PHOENIX) ||
      //     this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_SYMPHONY_PHOENIX) ||
      //     this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_SYMPHONY)
      //   ) {
      //     columns.add('target')
      //     columns.add('display_name')
      //     columns.add('advertiserBrand')
      //     columns.add('audience_size')
      //     columns.add('target_datasource')
      //     columns.add('cae_id')
      //     columns.add('created')
      //     columns.add('created_by')
      //     columns.add('flightstartdate')
      //     columns.add('flightenddate')
      //     columns.add('last_posting')
      //     columns.add('activation_status')
      //   }

      //   if (
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_KRUX) ||
      //     this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_LIVE_RAMP) ||
      //     this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_KRUX) ||
      //     this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_LIVE_RAMP) ||
      //     this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_KRUX) ||
      //     this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_LIVE_RAMP)
      //   ) {
      //     columns.add('target')
      //     columns.add('display_name')
      //     columns.add('campaign_name')
      //     columns.add('target_datasource')
      //     columns.add('cae_id')
      //     columns.add('created')
      //     columns.add('created_by')
      //     columns.add('flightstartdate')
      //     columns.add('flightenddate')
      //     columns.add('destinations')
      //     columns.add('activation_status')
      //   }

      //   if (this.isResearchProfileUser) {
      //     columns.delete('activation_date')
      //     columns.delete('viewing_stream')
      //   }

      //   return Array.from(columns)
      // },
      filterFromState() {
        return this.$store.state.appRes.filter
      },
      targetFilterFromState() {
        return this.$store.state.appRes.targetFilter
      },
      columnsFilterFromState() {
        return this.$store.getters.columnFilters || {}
      },
    },
    methods: {
      setColumnFilters(columnFilters, forceUpdate = false) {
        const gridFilterModel = this.gridApi.getFilterModel()
        let updatedColumnFilters = {}
        for (const c in columnFilters) {
          if (!Object.keys(gridFilterModel).includes(c) || forceUpdate) {
            const setFilter = this.gridApi.getFilterInstance(c)
            updatedColumnFilters[c] = columnFilters[c]
            switch (c) {
              case 'datasources':
                let sourcesFilter = []
                for (const key in SOURCES_FILTER) {
                  if (Array.isArray(SOURCES_FILTER[key]['value'])) {
                    if (SOURCES_FILTER[key]['value'].some(d => columnFilters[c].includes(d))) {
                      sourcesFilter.push(SOURCES_FILTER[key])
                    }
                  } else if (columnFilters[c].includes(SOURCES_FILTER[key]['value'])) {
                    sourcesFilter.push(SOURCES_FILTER[key])
                  }
                }
                setFilter.component.updateFilterValues(sourcesFilter.sort((a, b) => (a.name > b.name ? 1 : -1)))
                break
              case 'activation_status':
                setFilter.setFilterValues(columnFilters[c])
                break
              case 'destinations':
                let destinationsFilter = []
                for (const key in DESTINATIONS_FILTER) {
                  if (Array.isArray(DESTINATIONS_FILTER[key]['value'])) {
                    if (DESTINATIONS_FILTER[key]['value'].some(d => columnFilters[c].includes(d))) {
                      destinationsFilter.push(DESTINATIONS_FILTER[key])
                    }
                  } else if (columnFilters[c].includes(DESTINATIONS_FILTER[key]['value'])) {
                    destinationsFilter.push(DESTINATIONS_FILTER[key])
                  }
                }
                setFilter.component.updateFilterValues(destinationsFilter.sort((a, b) => (a.name > b.name ? 1 : -1)))
                break
            }
          }
        }
        this.$store.dispatch(SET_COLUMN_FILTERS, updatedColumnFilters)
      },
      setAppliedFiltersDisplay(filterModel) {
        const gridFilterModel = filterModel || this.gridApi.getFilterModel()
        this.appliedFilters = this.columnDefs.reduce((acc = [], cv) => {
          if (Object.keys(gridFilterModel).includes(cv.field)) {
            let f = gridFilterModel[cv.field]
            switch (f['filterType']) {
              case 'text':
                acc.push({ name: cv.headerName, value: this.getTextFilterValue(f), filterName: cv.field })
                break
              case 'number':
                acc.push({ name: cv.headerName, value: this.getNumberFilterValue(f), filterName: cv.field })
                break
              case 'date':
                acc.push({ name: cv.headerName, value: this.getDateFilterValue(f), filterName: cv.field })
                break
              case 'set':
                acc.push({
                  name: cv.headerName,
                  value: this.getSetFilterValue(f, cv.field !== 'destinations'),
                  filterName: cv.field,
                })
                break
            }
          }
          return acc
        }, [])
      },
      getSetFilterValue(f, uppercase = false) {
        if (f['values'].length === 1) {
          return uppercase ? `= ${f['values'][0].toUpperCase()}` : `= ${f['values'][0]}`
        } else {
          return uppercase ? `in (${f['values'].join(', ').toUpperCase()})` : `in (${f['values'].join(', ')})`
        }
      },
      getTextFilterValue(filter) {
        const filterValue = function (f) {
          switch (f['type']) {
            case 'equals':
              return `= ${f['filter']}`
            case 'notEqual':
              return `!= ${f['filter']}`
            case 'contains':
              return `~ ${f['filter']}`
            case 'notContains':
              return `!~ ${f['filter']}`
            case 'startsWith':
              return `starts with ${f['filter']}`
            case 'endsWith':
              return `ends with ${f['filter']}`
            default:
              return `${f['type']} ${f['filter']}`
          }
        }

        if (filter['operator']) {
          return filterValue(filter['condition1']) + ' ' + filter['operator'] + ' ' + filterValue(filter['condition2'])
        } else {
          return filterValue(filter)
        }
      },
      getNumberFilterValue(filter) {
        const filterValue = function (f) {
          switch (f['type']) {
            case 'equals':
              return `= ${f['filter']}`
            case 'notEqual':
              return `!= ${f['filter']}`
            case 'lessThan':
              return `< ${f['filter']}`
            case 'lessThanOrEqual':
              return `<= ${f['filter']}`
            case 'greaterThan':
              return `> ${f['filter']}`
            case 'greaterThanOrEqual':
              return `>= ${f['filter']}`
            case 'inRange':
              return `from ${f['filter']} to ${f['filterTo']}`
            default:
              return `${f['type']} ${f['filter']}`
          }
        }

        if (filter['operator']) {
          return filterValue(filter['condition1']) + ' ' + filter['operator'] + ' ' + filterValue(filter['condition2'])
        } else {
          return filterValue(filter)
        }
      },
      getDateFilterValue(filter) {
        let that = this
        const filterValue = function (f) {
          switch (f['type']) {
            case 'equals':
              return `= ${that.formatDate(f['dateFrom'])}`
            case 'notEqual':
              return `!= ${that.formatDate(f['dateFrom'])}`
            case 'lessThan':
              return `< ${that.formatDate(f['dateFrom'])}`
            case 'greaterThan':
              return `> ${that.formatDate(f['dateFrom'])}`
            case 'inRange':
              return `from ${that.formatDate(f['dateFrom'])} to ${that.formatDate(f['dateTo'])}`
            default:
              return `${f['type']} ${that.formatDate(f['dateFrom'])}`
          }
        }

        if (filter['operator']) {
          return filterValue(filter['condition1']) + ' ' + filter['operator'] + ' ' + filterValue(filter['condition2'])
        } else {
          return filterValue(filter)
        }
      },
      clearFilter(filter) {
        const gridFilterModel = this.gridApi.getFilterModel()
        const filterModel = { ...gridFilterModel }
        delete filterModel[filter.filterName]
        if (filter.filterName === 'destinations') {
          this.gridApi.destroyFilter('destinations')
        }
        if (filter.filterName === 'datasources') {
          this.gridApi.destroyFilter('datasources')
        }
        this.setAppliedFiltersDisplay(filterModel)
        this.setColumnFilters(this.columnsFilterFromState, true)
        this.$router.push({ query: { ...filterModel } }).catch(err => {
          // Ignore NavigationDuplicated error when navigating to the same route
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        })
        // TODO: Clear filters
      },
      getRowNodeId(data) {
        return data.id
      },
      setRowData(id, data) {
        const rowNode = this.gridApi.getRowNode(id)
        const updatedData = { ...rowNode.data, ...data, display_name: rowNode.data.display_name }
        rowNode.setData(updatedData)
      },
      onGridReady(params) {
        this.gridApi = params.api
        this.columnApi = params.columnApi
        let that = this
        const datasource = {
          getRows: params => {
            this.setAppliedFiltersDisplay()
            if (
              this.forceUpdateFilterValues ||
              (Object.keys(that.gridApi.getFilterModel()).length > 0 &&
                Object.keys(this.columnsFilterFromState).length > 0)
            ) {
              this.setColumnFilters(this.columnsFilterFromState, true)
              this.forceUpdateFilterValues = false
            }
            fetchTargets(
              params.request,
              this.routeParamsName,
              this.filters.archived && this.filters.archived.toString() === 'true',
            )
              .then(response => {
                // onRowDataChange
                if (response.data.rows.length === 0) {
                  that.gridApi.showNoRowsOverlay()
                } else {
                  that.gridApi.hideOverlay()
                  if (response.data.filters) {
                    this.setColumnFilters(response.data.filters)
                  }
                }
                params.successCallback(response.data.rows, response.data.rowCount)
              })
              .catch(error => {
                console.error(error)
                params.failCallback()
              })
          },
        }

        this.gridApi.setServerSideDatasource(datasource)
        let query = this.getFilterModelFromQuery()
        this.gridApi.setFilterModel(query)

        EventBus.$on('update-target', this.setRowData)
      },
      isExternalFilterPresent() {
        return true
      },
      doesExternalFilterPass(node) {
        return this.filters.archived && this.filters.archived.toString() === 'true'
          ? node.data.archived
          : !node.data.archived
      },
      onTargetFilterChange(e) {
        this.gridApi.ensureIndexVisible(0)
        const value = e
        const filterModel = this.getFilterModelFromQueryWithJSONStrings()
        this.changeFilter(value, filterModel)
        this.$store.dispatch(SET_DASHBOARD_TARGET_FILTER, value)
        this.gridApi.purgeServerSideCache()
      },
      changeFilter(value, filterModel) {
        if (value === actions.ALL_TARGETS) {
          // eslint-disable-next-line no-undef
          adobeAnalytics.selectAllTargets({
            username: this.username,
          })
          this.$router.push({ path: `/audience-hub/targets`, query: { ...filterModel } }).catch(err => {
            // Ignore NavigationDuplicated error when navigating to the same route
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        } else if (value === actions.MY_TARGETS) {
          // eslint-disable-next-line no-undef
          adobeAnalytics.selectMyTargets({
            username: this.username,
          })
          this.$router.push({ path: `/audience-hub/targets/${this.username}`, query: { ...filterModel } }).catch(err => {
            // Ignore NavigationDuplicated error when navigating to the same route
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        } else if (value === actions.ARCHIVED_TARGETS) {
          // eslint-disable-next-line no-undef
          adobeAnalytics.selectArchivedTargets({
            username: this.username,
          })
          this.$router.push({ path: `/audience-hub/targets`, query: { ...filterModel, archived: true } }).catch(err => {
            // Ignore NavigationDuplicated error when navigating to the same route
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        } else if (value === actions.MY_ARCHIVED_TARGETS) {
          // eslint-disable-next-line no-undef
          adobeAnalytics.selectArchivedTargets({
            username: this.username,
          })
          this.$router.push({
            path: `/audience-hub/targets/${this.username}`,
            query: { ...filterModel, archived: true },
          }).catch(err => {
            // Ignore NavigationDuplicated error when navigating to the same route
            if (err.name !== 'NavigationDuplicated') {
              throw err
            }
          })
        }
      },
      onFilterChanged(params) {
        const gridFilterModel = this.gridApi.getFilterModel()
        const filterModel = { ...gridFilterModel }
        for (const field of Object.keys(filterModel)) {
          if (typeof filterModel[field] === 'object') {
            filterModel[field] = JSON.stringify(filterModel[field])
          }
        }
        if (this.filters.archived === false) {
          delete filterModel.archived
        } else if (this.filters.archived) {
          filterModel.archived = this.filters.archived
        }

        // Check if the new query is different from current query to avoid NavigationDuplicated error
        const currentQuery = this.$route.query
        const newQuery = { ...filterModel }

        // Compare queries to see if navigation is necessary
        if (!this.areQueriesEqual(currentQuery, newQuery)) {
          this.$router.push({ query: newQuery }).catch(err => {
            // Ignore NavigationDuplicated error
            if (err.name !== 'NavigationDuplicated') {
              console.error('Navigation error:', err)
            }
          })
        }
        // this.gridApi.purgeServerSideCache()
      },
      areQueriesEqual(query1, query2) {
        // Helper method to compare two query objects
        const keys1 = Object.keys(query1 || {})
        const keys2 = Object.keys(query2 || {})

        if (keys1.length !== keys2.length) {
          return false
        }

        for (const key of keys1) {
          if (query1[key] !== query2[key]) {
            return false
          }
        }

        return true
      },
      onColumnEverythingChanged(e) {
        if (e.source === 'contextMenu') {
          this.setInitialColumnState()
        }
      },
      getFilterModelFromQuery() {
        const filterModel = { ...this.filters }
        delete filterModel.archived
        for (const field of Object.keys(filterModel)) {
          try {
            if (filterModel[field]) {
              filterModel[field] = JSON.parse(filterModel[field])
            } else {
              delete filterModel[field]
            }
          } catch (err) {
            continue
          }
        }
        return filterModel
      },
      getFilterModelFromQueryWithJSONStrings() {
        let filterModel = this.getFilterModelFromQuery()
        for (const field of Object.keys(filterModel)) {
          if (typeof filterModel[field] === 'object') {
            filterModel[field] = JSON.stringify(filterModel[field])
          }
        }
        return filterModel
      },
      getContextMenuItems(params) {
        const target = params.node && params.node.data
        let targetOptions = []

        if (target) {
          // it is disabled if submitted today or ACTIVATION_STATUS.ACTIVATED but not your target
          let disabled = false
          const targetActionRoutes = [
            {
              name: target.target === TARGET_TYPE.SD ? 'Activate' : 'Generate Estimate',
              value: OPTIONS.ACTIVATE,
              disabled,
            },
            {
              name: 'Open',
              value: OPTIONS.OPEN,
              disabled,
              action: () => {
                if (target.target === TARGET_TYPE.AD || target.target === TARGET_TYPE.SD) {
                  targetRouteHandler('OPEN', target)
                }
                if (target.target === TARGET_TYPE.LAS) {
                  this.$router.push({ name: 'NewWorkbench', params: { id: target.id } }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                      console.error('Navigation error:', err)
                    }
                  })
                }
                if (Object.values(this.C360Datasources).includes(target.target_datasource)) {
                  if (target.activation_status === ACTIVATION_STATUS.ACTIVATED) {
                    this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {
                      target_id: target.id,
                      option: LAS_TYPES.XANDR,
                      targetAction: TARGET_ACTIONS.UPDATE,
                    })
                  } else if (
                    target.metadata.intents &&
                    target.metadata.intents.some(i => i['Name'] === INTENTS['Sales']['Name'])
                  ) {
                    this.$router.push({ name: 'NewWorkbench', params: { id: target.id } }).catch(err => {
                      if (err.name !== 'NavigationDuplicated') {
                        console.error('Navigation error:', err)
                      }
                    })
                  } else {
                    this.$router.push({ name: 'XandrWorkbench', params: { id: target.id } }).catch(err => {
                      if (err.name !== 'NavigationDuplicated') {
                        console.error('Navigation error:', err)
                      }
                    })
                  }
                  return
                }
              },
            },
            {
              name: 'Duplicate',
              value:
                target.target === TARGET_TYPE.LAS && target.activation_status === ACTIVATION_STATUS.ACTIVATED
                  ? null
                  : OPTIONS.DUPLICATE,
              disabled: target.target === TARGET_TYPE.LAS && target.activation_status === ACTIVATION_STATUS.ACTIVATED,
              action: () => {
                if (Object.values(this.C360Datasources).includes(target.target_datasource) || target.target === TARGET_TYPE.LAS) {
                  this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {
                    option: LAS_TYPES.XANDR,
                    target_id: target.id,
                    targetAction: TARGET_ACTIONS.DUPLICATE,
                  })
                } else if (
                  target.target === TARGET_TYPE.AD &&
                  target.target_datasource === this.nielsenDatasources.AMRLD
                ) {
                  this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {
                    option: this.nielsenDatasources.AUDIENCE_DISCOVERY,
                    target_id: target.id,
                    targetAction: TARGET_ACTIONS.DUPLICATE,
                  })
                } else {
                  this.$store.dispatch(SET_BACK_ROUTE_OVERRIDE, this.$router.currentRoute.path)
                  targetRouteHandler(OPTIONS.DUPLICATE, target)
                }
              },
            },
            {
              name: 'Archive Target',
              value: OPTIONS.ARCHIVE,
              disabled,
              action: () => {
                this.$store.dispatch(ARCHIVE_TARGET, target)
              },
            },
            {
              name: 'Unarchive Target',
              value: OPTIONS.UNARCHIVE,
              disabled,
              action: () => {
                this.$store.dispatch(UNARCHIVE_TARGET, target)
              },
            },
            {
              name: 'Download Graph Package',
              value: OPTIONS.GRAPH,
              disabled,
              action: () => {
                this.$store.dispatch(DOWNLOAD_LAS_TARGET, target)
              },
            },
            {
              name: 'Settings',
              value: OPTIONS.SETTINGS,
              disabled,
            },
            {
              name: 'Delete',
              value: OPTIONS.DELETE,
              disabled,
              action: () => {
                this.$store.dispatch(SET_CURRENT_TARGET, target)
                this.$store.dispatch(SHOW_DELETE_TARGET_MODAL)
              },
            },
            {
              name: 'Re-Forecast Target',
              value: OPTIONS.RE_POST,
              disabled,
              action: () => {
                this.$store.dispatch(SHOW_REPOSSTING_MODAL, target)
              },
            },
            {
              name: 'Download Target Definition',
              value: OPTIONS.DEFINITION,
              disabled,
              action: () => {
                this.$store.dispatch(DOWNLOAD_TARGET_DEFINITION, {
                  targetId: target.id,
                })
              },
            },
            {
              name: 'Download PID Lists',
              value: OPTIONS.PID_DOWNLOAD,
              disabled,
              action: () => {
                this.$store.dispatch(SET_SHOW_PID_DOWNLOAD_MODAL, target)
              },
            },
            {
              name: 'Append File',
              value: OPTIONS.APPEND,
              disabled,
              action: () => {
                // TODO fix this hack up...
                if (
                  (!target.archived && target.target === TARGET_TYPE.NPOWER) ||
                  target.target === TARGET_TYPE.COMSCORE
                ) {
                  this.$store.dispatch(SET_SHOW_APPEND_MODAL, { target })
                } else if (target.target === TARGET_TYPE.PID) {
                  this.$store.dispatch(SET_UPLOAD_MODAL_OPTIONS, {
                    type: '2',
                    appendingOption: TARGET_TYPE.PID,
                    target: target,
                  })
                  this.$store.dispatch(APPEND_PID)
                }
              },
            },
            {
              name: 'Update Flight Dates',
              value: OPTIONS.UPDATE_LAS_MODAL,
              disabled,
              action: () => {
                this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {
                  target_id: target.id,
                  option: LAS_TYPES.XANDR,
                  targetAction: TARGET_ACTIONS.UPDATE,
                })
              },
            },
            {
              name: 'Update Campaign Data',
              value: OPTIONS.UPDATE_CAMPAIGN_DATA_MODAL,
              disabled,
              action: () => {
                this.$store.dispatch(SET_STEPPER_DATA_SOURCE, {
                  target_id: target.id,
                  option: LAS_TYPES.XANDR,
                  targetAction: TARGET_ACTIONS.UPDATE_CAMPAIGN,
                })
              },
            },
            {
              name: 'Send Downstream',
              value: OPTIONS.SEND_DOWNSTREAM,
              disabled,
              action: () => {
                if (target.target === TARGET_TYPE.NPOWER) {
                  this.$store.dispatch(REFRESH_TABLE, false)
                  this.$store.dispatch(SEND_NPOWER_DOWNSTREAM, { target_id: target.id }).then(res => {
                    if (res) {
                      this.$store.dispatch(REFRESH_TABLE, true)
                    }
                  })
                } else if (target.target === TARGET_TYPE.COMSCORE) {
                  this.$store.dispatch(REFRESH_TABLE, false)
                  this.$store.dispatch(SEND_COMSCORE_DOWNSTREAM, { target_id: target.id }).then(res => {
                    if (res) {
                      this.$store.dispatch(REFRESH_TABLE, true)
                    }
                  })
                }
              },
            },
          ]

          /**
           * included - this is the list to include options in the targetOptions
           * disableList - this is the list of options to disable in the targetOptions, if they exist in the included list
           */
          let included = []
          let disableList = []

          // TODO make this easier to read, maybe use target classes that manage it instead of all these if
          // statements...
          switch (target.target) {
            case TARGET_TYPE.LAS:
              included = [OPTIONS.ACTIVATE, OPTIONS.OPEN, OPTIONS.DUPLICATE, OPTIONS.UNARCHIVE]
              if (target.activation_status === ACTIVATION_STATUS.LIVE) {
                included = [OPTIONS.OPEN, OPTIONS.DUPLICATE, OPTIONS.GRAPH, OPTIONS.DEFINITION]
                if (this.hasPublishPermissions && target.destinations.includes(DESTINATIONS.SYMPHONY)) {
                  included.push(OPTIONS.DELETE)
                }
                target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
              }
              if (target.activation_status === ACTIVATION_STATUS.WORKING) {
                included = [OPTIONS.OPEN, OPTIONS.DUPLICATE, OPTIONS.GRAPH, OPTIONS.DEFINITION]
                if (this.hasPublishPermissions && target.destinations.includes(DESTINATIONS.SYMPHONY)) {
                  included.push(OPTIONS.DELETE)
                  target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
                }
                if (
                  target.destinations.length === 0 &&
                  target.activation_status === ACTIVATION_STATUS.WORKING &&
                  isTargetOwnedBy(target, this.username)
                ) {
                  included.push(OPTIONS.DELETE)
                  target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
                }
              }
              if (target.activation_status === ACTIVATION_STATUS.ACTIVATED) {
                included = []
                target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
              }
              if (
                target.activation_status !== ACTIVATION_STATUS.LIVE &&
                target.target_definition &&
                target.target_definition.query
              ) {
                included.push(OPTIONS.DEFINITION)
                included.push(OPTIONS.GRAPH)
              }
              if (target.activation_status === ACTIVATION_STATUS.FAILED) {
                included = [
                  OPTIONS.OPEN,
                  OPTIONS.DUPLICATE,
                  OPTIONS.GRAPH,
                  OPTIONS.DEFINITION,
                  OPTIONS.PID_DOWNLOAD,
                  OPTIONS.DELETE,
                ]
                target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
              }
              break
            case TARGET_TYPE.AD:
              const marketingIntent =
                target.metadata &&
                target.metadata.intents &&
                target.metadata.intents.length &&
                target.metadata.intents.find(el => el.IntentId === INTENTS.Marketing.IntentId)
              const researchIntent =
                target.metadata &&
                target.metadata.intents &&
                target.metadata.intents.length &&
                target.metadata.intents.find(el => el.IntentId === INTENTS.Research.IntentId)
              included = [OPTIONS.OPEN]
              if (marketingIntent) target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
              if (marketingIntent) {
                included.push(OPTIONS.DUPLICATE)
                target.archived ? included.push(OPTIONS.UNARCHIVE) : included.push(OPTIONS.ARCHIVE)
              } else {
                if (researchIntent) included.push(OPTIONS.DUPLICATE)
              }
              if (target.activation_status === ACTIVATION_STATUS.WORKING) included.push(OPTIONS.DELETE)
              break
            case TARGET_TYPE.SD:
              included = [
                OPTIONS.ACTIVATE,
                OPTIONS.OPEN,
                OPTIONS.DUPLICATE,
                OPTIONS.ARCHIVE,
                OPTIONS.UNARCHIVE,
                OPTIONS.DELETE,
              ]
              if (!target.archived) included.push(OPTIONS.SETTINGS)
              break
            case TARGET_TYPE.COMSCORE_TV:
              included = [OPTIONS.ACTIVATE, OPTIONS.UNARCHIVE]
              if (target.activation_status !== ACTIVATION_STATUS.WORKING) included.push(OPTIONS.ARCHIVE)
              break
            case TARGET_TYPE.NPOWER:
            case TARGET_TYPE.COMSCORE:
              included = [OPTIONS.ARCHIVE, OPTIONS.UNARCHIVE]
              if (
                !target.archived &&
                target.activation_status !== ACTIVATION_STATUS.FAILED &&
                this.hasPublishPermissions
              ) {
                included.push(OPTIONS.APPEND)
              }
              if (
                target.activation_status !== ACTIVATION_STATUS.FAILED &&
                target.activation_status !== ACTIVATION_STATUS.LIVE &&
                !target.archived &&
                this.hasPublishPermissions
              ) {
                included.push(OPTIONS.SEND_DOWNSTREAM)
              }
              break
            case TARGET_TYPE.PID:
              included = [OPTIONS.ARCHIVE, OPTIONS.UNARCHIVE]
              if (target.destinations.includes(DESTINATIONS.SYMPHONY)) {
                included.push(OPTIONS.PID_DOWNLOAD)
              }
              if (this.hasPublishPermissions) {
                included.push(OPTIONS.APPEND)
              }
              break
            case TARGET_TYPE.ADSTRA:
            case TARGET_TYPE.XANDR_SEGMENT:
            case TARGET_TYPE.BYOD:
            case TARGET_TYPE.PRISM:
            case TARGET_TYPE.DNB:
            case TARGET_TYPE.MAX:
            case TARGET_TYPE.TVE:
            case TARGET_TYPE.EPSILON:
            case TARGET_TYPE.DISCOVERY_PLUS:
            case TARGET_TYPE.DCR:
            case TARGET_TYPE.VIDEOAMP:
            case TARGET_TYPE.LIVERAMP:
            case TARGET_TYPE.WBGAMES:
            case TARGET_TYPE.RETARGETING:
            case TARGET_TYPE.FREEWHEEL:
            case TARGET_TYPE.GOOGLE_AD_MANAGER:
            case TARGET_TYPE.LAL:
            case TARGET_TYPE.DC_COMICS:
            case TARGET_TYPE.CNN:
              const isAddressableOrDigital =
                target.destinations.includes(DESTINATIONS.DIRECTV) ||
                target.destinations.includes(DESTINATIONS.VIZIO) ||
                target.destinations.includes(DESTINATIONS.CANOE_LINEAR) ||
                target.destinations.includes(DESTINATIONS.CANOE_VOD) ||
                target.destinations.includes(DESTINATIONS.FREEWHEEL) ||
                target.destinations.includes(DESTINATIONS.GOOGLE_AD_MANAGER) ||
                target.destinations.includes(DESTINATIONS.VIDEOAMP)
              const isSDXandrTarget =
                target.destinations.includes(DESTINATIONS.KRUX) || target.destinations.includes(DESTINATIONS.LIVERAMP)
              switch (target.activation_status) {
                case ACTIVATION_STATUS.ACTIVATED:
                  included = [OPTIONS.DELETE, OPTIONS.ARCHIVE]
                  // Do not show Open in context menu for Xandr Addressable targets in Activated status
                  if (!isAddressableOrDigital) {
                    included.push(OPTIONS.OPEN)
                  }
                  break
                case ACTIVATION_STATUS.WORKING:
                  included = [OPTIONS.OPEN, OPTIONS.DUPLICATE, OPTIONS.ARCHIVE]
                  if (isSDXandrTarget) {
                    included.push(OPTIONS.UPDATE_CAMPAIGN_DATA_MODAL)
                  }
                  if (
                    this.hasPublishPermissions ||
                    (!this.hasPublishPermissions && isTargetOwnedBy(target, this.username))
                  ) {
                    included.push(OPTIONS.DELETE)
                  }
                  break
                case ACTIVATION_STATUS.SUBMITTED:
                  if (target.destinations.includes(DESTINATIONS.SYMPHONY) || isAddressableOrDigital) {
                    included = [OPTIONS.OPEN, OPTIONS.DUPLICATE]
                  }
                  if (!isAddressableOrDigital) {
                    included.push(OPTIONS.UPDATE_LAS_MODAL)
                    included.push(OPTIONS.ARCHIVE)
                  }

                  break
                case ACTIVATION_STATUS.LIVE:
                  included = [OPTIONS.OPEN, OPTIONS.DUPLICATE]
                  if (target.destinations.includes(DESTINATIONS.SYMPHONY)) {
                    included.push(OPTIONS.UPDATE_LAS_MODAL)
                  }
                  break
                case ACTIVATION_STATUS.FAILED:
                  included = [OPTIONS.ARCHIVE]
                  break
                case ACTIVATION_STATUS.CANCELLED:
                  included = [OPTIONS.OPEN, OPTIONS.DUPLICATE]
                  if (!isAddressableOrDigital) {
                    included.push(OPTIONS.UPDATE_LAS_MODAL)
                  }
                  break
                case ACTIVATION_STATUS.MODELING:
                  included = [OPTIONS.OPEN, OPTIONS.DUPLICATE]
                  break
                case ACTIVATION_STATUS.MODELED:
                  included = [OPTIONS.OPEN, OPTIONS.ARCHIVE, OPTIONS.DUPLICATE]
                  break
              }
              if (isAddressableOrDigital && !this.hasCreateEditPermissions) {
                let removeList = [OPTIONS.DELETE, OPTIONS.DUPLICATE, OPTIONS.UPDATE_LAS_MODAL, OPTIONS.ARCHIVE]
                included = included.filter(i => removeList.indexOf(i) === -1)
              }
              if (isSDXandrTarget) {
                included = included.filter(i => i !== OPTIONS.DUPLICATE)
              }
              break
          }
          if (target.archived) {
            included = included.filter(option => option !== OPTIONS.ARCHIVE)
          } else {
            included = included.filter(option => option !== OPTIONS.UNARCHIVE)
          }

          const repostable =
            ((target.target === TARGET_TYPE.LAS && !target.destinations.includes(DESTINATIONS.SYMPHONY)) ||
              target.target === TARGET_TYPE.COMSCORE_TV) &&
            target.activated
          if (repostable && this.hasPublishPermissions) {
            included.push(OPTIONS.RE_POST)
          }

          if (!this.hasCreateEditPermissions) {
            const createEditOptions = [OPTIONS.DUPLICATE, OPTIONS.DELETE]
            included = included.filter(i => createEditOptions.indexOf(i) === -1)
          }

          if (!this.hasArchivePermissions) {
            const archiveOptions = [OPTIONS.ARCHIVE, OPTIONS.UNARCHIVE]
            included = included.filter(i => archiveOptions.indexOf(i) === -1)
          }

          if (!this.hasPublishPermissions) {
            const appendOption = [OPTIONS.APPEND]
            included = included.filter(i => appendOption.indexOf(i) === -1)
          }

          targetOptions = cloneDeep(targetActionRoutes.filter(r => included.includes(r.value)))

          let isActivatedOrtargetIsNotUser = target.activated || !isTargetOwnedBy(target, this.username)
          if (isActivatedOrtargetIsNotUser) {
            // do not include activate, settings, and delete
            targetOptions = targetOptions.filter(
              t => ![OPTIONS.ACTIVATE, OPTIONS.SETTINGS, OPTIONS.DELETE].includes(t.value),
            )
          } else if (target.activation_status === ACTIVATION_STATUS.ACTIVATED) {
            targetOptions = targetOptions.filter(t => ![OPTIONS.ACTIVATE, OPTIONS.SETTINGS].includes(t.value))
          } else if (isActivationBlocked(target.target)) {
            targetOptions = targetOptions.filter(t => t.value !== OPTIONS.ACTIVATE)
          }
          let targetIsXandrAndNotSubmittedOrAbove =
            (target.target === TARGET_TYPE.XANDR_SEGMENT || target.target === TARGET_TYPE.ADSTRA) &&
            (target.activation_status === ACTIVATION_STATUS.ACTIVATED ||
              target.activation_status === ACTIVATION_STATUS.WORKING)
          if (targetIsXandrAndNotSubmittedOrAbove) {
            targetOptions.push(OPTIONS.DELETE)
          }
          // we need to disable the targetOptions
          if (disableList.length > 0) {
            for (let dis of disableList) {
              for (let op of targetOptions) {
                if (op.value === dis) {
                  op.disabled = true
                }
              }
            }
          }
        }

        return [
          ...targetOptions,
          'separator',
          'copy',
          'copyWithHeaders',
          'separator',
          // {
          //   name: 'Set Default Filters',
          //   action: () => {
          //     this.setInitialFilter(true)
          //   }
          // },
          {
            name: 'Clear All Filters',
            action: () => {
              const query = {}
              if (this.filters.archived) {
                query.archived = this.filters.archived
              }
              this.gridApi.destroyFilter('destinations')
              this.$router.push({ query }).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  console.error('Navigation error:', err)
                }
              })
              this.gridApi.setFilterModel(null)
            },
          },
        ]
      },
      formatDate(str, utc) {
        return dateFormatter(str, utc)
      },
      clearDownloadedContent() {
        this.$store.dispatch(CLEAR_TARGET_DOWNLOAD)
      },
      formatSmallNumber(amount) {
        if (!amount || isNaN(amount)) {
          return ''
        }
        if (amount >= 1000000) {
          let quotient = amount / 1000000
          return (this.isDecimal(quotient) ? quotient.toFixed(quotient > 10 ? 1 : 2) : quotient) + 'M'
        } else if (amount >= 1000) {
          let quotient = amount / 1000
          return (this.isDecimal(quotient) ? quotient.toFixed(1) : quotient) + 'K'
        } else {
          return this.isDecimal(amount) ? amount.toFixed(1) : amount
        }
      },
      isDecimal(num) {
        let isDec = num % 1 !== 0
        return num % 1 !== 0
      },
      // convertStream (viewing_stream) {
      //   return convertViewingStreamForDisplay(viewing_stream)
      // },
      // Deprecated by AM-3571. As profiles are restructured with AM-3571, setting initial filter is no longer needed
      // setInitialFilter (force) {
      //   let filterModel = {}
      //   if (!force) {
      //     // we can set the filer here....
      //     filterModel = this.getFilterModelFromQueryWithJSONStrings()
      //   }

      //   if (this.filters.archived) {
      //     filterModel.archived = this.filters.archived
      //   }

      //   if (!filterModel.destinations || JSON.parse(filterModel.destinations).values.length === 0 || force) {
      //     let destinations = {
      //       values: [],
      //       filterType: 'set'
      //     }

      //     if (
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_MRI_SYMPHONY_CAE) ||
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_NCS_SYMPHONY_CAE) ||
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_AMRLD_SYMPHONY_CAE) ||
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_SYMPHONY_CAE) ||
      //       this.permissions.includes(PERMISSIONS_OLD.MARKETING_MRI_SYMPHONY_PHOENIX) ||
      //       this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_SYMPHONY_PHOENIX) ||
      //       this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_SYMPHONY)
      //     ) {
      //       destinations.values = [DESTINATIONS.BLANK, DESTINATIONS.SYMPHONY, DESTINATIONS.DIRECTV, DESTINATIONS.VIZIO, DESTINATIONS.CANOE_LINEAR, DESTINATIONS.CANOE_VOD]
      //     }

      //     if (
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_KRUX) ||
      //       this.permissions.includes(PERMISSIONS_OLD.AD_SALES_XANDR_LIVE_RAMP) ||
      //       this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_KRUX) ||
      //       this.permissions.includes(PERMISSIONS_OLD.MARKETING_XANDR_LIVE_RAMP) ||
      //       this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_KRUX) ||
      //       this.permissions.includes(PERMISSIONS_OLD.RESEARCH_XANDR_LIVE_RAMP)
      //     ) {
      //       if (destinations.values.includes(DESTINATIONS.SYMPHONY)) {
      //         destinations = undefined
      //         delete filterModel.destinations
      //       } else {
      //         destinations.values = [
      //           DESTINATIONS.KRUX,
      //           DESTINATIONS.LIVERAMP,
      //           `${DESTINATIONS.KRUX}, ${DESTINATIONS.LIVERAMP}`
      //         ]
      //       }
      //     }
      //     if (filterModel.destinations) {
      //       let dest = JSON.parse(filterModel.destinations)
      //       if (dest && dest.values.length > 0) {
      //         let newDestinations = []
      //         for (let d of dest) {
      //           if (destinations.values.includes(d)) {
      //             newDestinations.push(d)
      //           }
      //         }
      //         destinations.values = newDestinations
      //       }
      //     }
      //     if (destinations && destinations.values.length > 0) {
      //       this.$router.push({query: {...filterModel, destinations: JSON.stringify(destinations)}})
      //     } else {
      //       this.$router.push({query: {...filterModel}})
      //     }
      //   }
      // },
      getNameFromUsername(username) {
        if (!username) {
          return ''
        }
        const emailParts = username.split('@')
        const nameParts = emailParts[0].split(/\.|_/)
        const first = capitalize(nameParts[0])
        const last = capitalize(nameParts[1])
        return `${first} ${last}`
      },
      setInitialColumnState() {
        if (this.columnApi) {
          const columnState = this.columnApi.getColumnState().map(column => {
            column.hide = !this.hasViewPermissions
            return column
          })
          this.columnApi.setColumnState(columnState)
        }
      },
      setFilter() {
        this.$store.dispatch(SET_DASHBOARD_FILTER, this.filters)
      },
      redirectToFilter() {
        const targetFilter = this.targetFilterFromState
        this.changeFilter(targetFilter || actions.MY_TARGETS, this.filterFromState)
      },
      resizeHandler() {
        this.gridApi.purgeServerSideCache()
      },
    },
    mounted() {
      this.clearDownloadedContent()
      let filters = this.filterFromState
      if (!filters) {
        this.setFilter()
      } else {
        this.redirectToFilter()
        this.forceUpdateFilterValues = true
      }
      window.addEventListener('resize', this.resizeHandler)
    },
    destroyed() {
      window.addEventListener('resize', this.resizeHandler)
    },
    watch: {
      filters() {
        this.setFilter()
        if (this.gridApi) {
          let filterModel = this.getFilterModelFromQuery()
          this.gridApi.setFilterModel(filterModel)
        }
      },
      refreshTable(value) {
        if (value) {
          this.gridApi.purgeServerSideCache()
        }
      },
      permissions() {
        // this.setInitialFilter()
        this.setInitialColumnState()
      },
    },
  }
</script>

<style lang="scss">
  $bubble_image_size: 30px;

  .ag-header {
    background-color: #f5f7f7 !important;
    border-top: solid 1px;
    border-bottom: solid 1px !important;
    border-top-color: #bdc3c7;
    border-bottom-color: #bdc3c7 !important;
  }

  .ag-header-row {
    color: rgba(0, 0, 0, 0.54);
    height: 36px !important;
  }

  .ag-header-cell {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .ag-cell {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .audience-hub-table {
    display: flex;
    flex-direction: column;
    grid-area: table;
    height: 100%;
    color: black;

    &__targets-select-container {
      margin: 10px 12px;
    }

    &__text-cell {
      display: flex;
      align-items: center;
    }

    &__multiline-text-cell {
      white-space: pre;
      line-height: normal !important;
    }

    &__center-text-cell {
      justify-content: center;
    }

    &__bubble-chart-image {
      height: $bubble_image_size;
      width: $bubble_image_size;
      float: left;
    }

    &__target-name {
      display: block;

      overflow: hidden;
      text-overflow: ellipsis;

      &:hover {
        cursor: pointer;
        color: color(yellow);
      }
    }

    &__more-icon {
      display: block;
      height: 25px;
      fill: white;
      cursor: pointer;

      &:hover {
        fill: color(yellow);
      }
    }

    &__status-flag.is-activated {
      padding: 5px 12px;
      border-radius: 3px;
      background-color: color(green);
      color: #ffffff;
      font-size: rem(11);
      text-transform: uppercase;
    }

    // overrides
    .action-button {
      height: auto;
      padding: 5px 12px;
      font-size: rem(12);
    }

    &__create-new-target-button {
      margin: 1% 0% 4% 40%;
    }

    &__button-action {
      background-color: rgb(36, 43, 50);
      display: inline-block !important;
    }
  }
</style>



// WEBPACK FOOTER //
// AudienceHubTable.vue?71c26884